import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { structuredLogger } from "@/lib/logging";

type ThemePreference = "light" | "dark";

const STORAGE_KEY = "gglife.theme";
const DEFAULT_THEME: ThemePreference = "light";

interface DarkModeContextType {
  isDark: boolean;
  toggleDarkMode: () => void;
  setTheme: (theme: ThemePreference) => void;
}

const DarkModeContext = createContext<DarkModeContextType>({
  isDark: false,
  toggleDarkMode: () => {},
  setTheme: () => {},
});

/**
 * Get stored theme from localStorage
 */
const getStoredTheme = (): ThemePreference | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }
    // Migration: check old key
    const oldStored = localStorage.getItem("darkMode");
    if (oldStored !== null) {
      const migrated: ThemePreference = oldStored === "true" ? "dark" : "light";
      localStorage.setItem(STORAGE_KEY, migrated);
      localStorage.removeItem("darkMode");
      return migrated;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Store theme in localStorage
 */
const storeTheme = (theme: ThemePreference): void => {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    structuredLogger.warn("Failed to store theme preference", { error: e instanceof Error ? e.message : "Unknown" });
  }
};

/**
 * Apply theme to document
 */
const applyTheme = (isDark: boolean): void => {
  const root = document.documentElement;
  if (isDark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

export const DarkModeProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [isDark, setIsDark] = useState<boolean>(() => {
    // Initial state: check localStorage, default to light
    const stored = getStoredTheme();
    return stored === "dark";
  });
  const [profileSynced, setProfileSynced] = useState(false);

  // Apply theme immediately on state change
  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  // Sync with user profile when authenticated
  useEffect(() => {
    if (authLoading || !user || profileSynced) return;

    const syncWithProfile = async () => {
      try {
        // Fetch user's theme preference from profile
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("theme_preference")
          .eq("user_id", user.id)
          .single();

        if (error) {
          structuredLogger.warn("Failed to fetch theme preference from profile", { 
            error: error.message 
          });
          setProfileSynced(true);
          return;
        }

        const profileTheme = profile?.theme_preference as ThemePreference | null;
        const localTheme = getStoredTheme();

        if (profileTheme === "light" || profileTheme === "dark") {
          // Profile has theme preference - use it as source of truth
          setIsDark(profileTheme === "dark");
          storeTheme(profileTheme);
        } else if (localTheme) {
          // Profile has no preference, but localStorage does - promote to profile
          await supabase
            .from("profiles")
            .update({ theme_preference: localTheme })
            .eq("user_id", user.id);
          
          setIsDark(localTheme === "dark");
        }
        // If neither has preference, stay with default (light)

        setProfileSynced(true);
      } catch (e) {
        structuredLogger.error("Error syncing theme preference", { 
          error: e instanceof Error ? e.message : "Unknown" 
        });
        setProfileSynced(true);
      }
    };

    syncWithProfile();
  }, [user, authLoading, profileSynced]);

  // Reset profile sync flag when user changes
  useEffect(() => {
    if (!user) {
      setProfileSynced(false);
    }
  }, [user]);

  const setTheme = useCallback(async (theme: ThemePreference) => {
    setIsDark(theme === "dark");
    storeTheme(theme);

    // If authenticated, persist to profile
    if (user) {
      try {
        await supabase
          .from("profiles")
          .update({ theme_preference: theme })
          .eq("user_id", user.id);
      } catch (e) {
        structuredLogger.warn("Failed to persist theme to profile", { 
          error: e instanceof Error ? e.message : "Unknown" 
        });
      }
    }
  }, [user]);

  const toggleDarkMode = useCallback(() => {
    const newTheme: ThemePreference = isDark ? "light" : "dark";
    setTheme(newTheme);
  }, [isDark, setTheme]);

  return (
    <DarkModeContext.Provider value={{ isDark, toggleDarkMode, setTheme }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkModeContext = () => useContext(DarkModeContext);
